import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Calendar } from "../components/ui/calendar";
import { Sparkles, Clock, CreditCard } from "lucide-react";
import { ptBR } from "date-fns/locale";

const PAY_METHODS = [
  { id: "pay_at_venue", label: "Pagamento na hora, na loja", detail: "Pix, cartão ou dinheiro no dia do atendimento" },
];

export default function BookingPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [date, setDate] = useState(() => {
    const p = params.get("date");
    if (!p) return new Date();
    const [y, m, d] = p.split("-").map(Number);
    return new Date(y, m - 1, d);
  });
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [payment, setPayment] = useState("pay_at_venue");
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date(date));
  const [avail, setAvail] = useState({});

  useEffect(() => {
    const m = `${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,"0")}`;
    api.get("/slots/month", { params: { month: m } }).then(r => setAvail(r.data)).catch(() => setAvail({}));
  }, [month, slots]);

  const dayGroups = useMemo(() => {
    const g = { free: [], half: [], full: [] };
    Object.entries(avail).forEach(([k, v]) => { const [y, mo, d] = k.split("-").map(Number); g[v.status]?.push(new Date(y, mo-1, d)); });
    return g;
  }, [avail]);
  const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  useEffect(() => {
    api.get("/services").then(r => {
      setServices(r.data);
      if (!serviceId && r.data[0]) setServiceId(r.data[0].service_id);
    });
  }, []);

  const selected = useMemo(() => services.find(s => s.service_id === serviceId), [services, serviceId]);

  useEffect(() => {
    if (!serviceId || !date) return;
    const y = date.getFullYear(), m = String(date.getMonth()+1).padStart(2,"0"), d = String(date.getDate()).padStart(2,"0");
    setSlot("");
    api.get("/slots", { params: { date: `${y}-${m}-${d}`, service_id: serviceId } })
      .then(r => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [serviceId, date]);

  const confirm = async () => {
    if (!user) {
      toast.error("Faça login para agendar");
      nav("/login"); return;
    }
    if (!slot) { toast.error("Escolha um horário"); return; }
    setBusy(true);
    try {
      await api.post("/appointments", { service_id: serviceId, start_at: slot, payment_method: payment });
      toast.success("Agendamento confirmado com sucesso!");
      nav("/perfil");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao agendar");
      setDate(new Date(date));
    } finally { setBusy(false); }
  };

  const joinWaitlist = async () => {
    if (!user) { toast.error("Faça login para entrar na lista de espera"); nav("/login"); return; }
    try {
      await api.post("/waitlist", { date: keyOf(date), service_id: serviceId });
      toast.success("Você entrou na lista de espera! Avisaremos no seu perfil se abrir uma vaga.");
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível entrar na lista"); }
  };

  const fmtHour = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="tag-sun mb-3 inline-block">AGENDAMENTO</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Reserve seu horário</h1>
        <p className="text-[color:var(--ink-500)] mt-3">Cancelamento gratuito com no mínimo 48h de antecedência.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-arya p-6">
          <div className="flex items-center gap-2 mb-4"><Sparkles size={16} className="text-[color:var(--sun-600)]"/><h3 className="font-serif text-xl">1. Especialidade</h3></div>
          <div className="grid gap-3">
            {services.map(s => (
              <button key={s.service_id}
                onClick={() => setServiceId(s.service_id)}
                aria-pressed={serviceId===s.service_id}
                data-testid={`pick-service-${s.service_id}`}
                className={`text-left rounded-xl border p-4 transition ${serviceId===s.service_id ? 'border-[color:var(--sun-600)] bg-[rgba(233,204,133,.12)]' : 'border-[var(--line)] hover:border-[color:var(--sun-500)]'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold">{s.title}</span>
                  <span className="font-serif text-[color:var(--sun-700)]">R$ {s.price.toFixed(2).replace('.',',')}</span>
                </div>
                <p className="text-xs text-[color:var(--ink-500)] mt-1">{s.duration_min} min · {s.category}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="card-arya p-6">
          <div className="flex items-center gap-2 mb-4"><Clock size={16} className="text-[color:var(--sun-600)]"/><h3 className="font-serif text-xl">2. Data & horário</h3></div>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d)=> d && setDate(d)}
              locale={ptBR}
              month={month}
              onMonthChange={setMonth}
              disabled={(d)=> d < new Date(new Date().setHours(0,0,0,0))}
              modifiers={dayGroups}
              modifiersClassNames={{ free: "day-free", half: "day-half", full: "day-full" }}
              className="rounded-xl"
              data-testid="booking-calendar"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-3 text-xs text-[color:var(--ink-500)]" data-testid="calendar-legend">
            <span className="inline-flex items-center gap-1"><i className="legend-dot bg-[#4A9B5C]"/> Vagas disponíveis</span>
            <span className="inline-flex items-center gap-1"><i className="legend-dot bg-[#E0B23A]"/> Metade preenchida</span>
            <span className="inline-flex items-center gap-1"><i className="legend-dot bg-[#C2453D]"/> Lotado</span>
          </div>
          <div className="mt-5">
            <p className="text-xs tracking-widest text-[color:var(--sun-700)] font-display mb-2">HORÁRIOS DISPONÍVEIS · 9h às 18h</p>
            <div className="grid grid-cols-4 gap-2">
              {slots.length === 0 && (
                <div className="col-span-4 text-sm text-[color:var(--ink-500)] space-y-2" data-testid="no-slots">
                  <p>Nenhum horário disponível neste dia.</p>
                  {date >= new Date(new Date().setHours(0,0,0,0)) && serviceId && (
                    <button onClick={joinWaitlist} className="btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="btn-join-waitlist">
                      <Clock size={14}/> Entrar na lista de espera
                    </button>
                  )}
                </div>
              )}
              {slots.map(s => (
                <button key={s} onClick={()=> setSlot(s)} aria-pressed={slot===s}
                        className="slot-chip text-sm" data-testid={`slot-${s}`}>{fmtHour(s)}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card-arya p-6 mt-6">
        <div className="flex items-center gap-2 mb-4"><CreditCard size={16} className="text-[color:var(--sun-600)]"/><h3 className="font-serif text-xl">3. Forma de pagamento</h3></div>
        <p className="text-xs text-[color:var(--ink-500)] mb-4">Todo pagamento é realizado presencialmente no estúdio.</p>
        <div className="grid md:grid-cols-3 gap-3">
          {PAY_METHODS.map(p => (
            <button key={p.id} onClick={() => setPayment(p.id)} aria-pressed={payment===p.id}
                    data-testid={`pay-${p.id}`}
                    className={`text-left rounded-xl border p-4 transition ${payment===p.id ? 'border-[color:var(--sun-600)] bg-[rgba(233,204,133,.12)]' : 'border-[var(--line)] hover:border-[color:var(--sun-500)]'}`}>
              <div className="font-display font-semibold">{p.label}</div>
              <div className="text-xs text-[color:var(--ink-500)] mt-1">{p.detail}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 card-arya p-6">
        <div>
          <p className="text-xs tracking-widest text-[color:var(--sun-700)] font-display">RESUMO</p>
          <p className="font-serif text-xl">{selected?.title || "—"}</p>
          <p className="text-sm text-[color:var(--ink-500)]">
            {slot ? new Date(slot).toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short", timeZone: "America/Sao_Paulo" }) : "Escolha um horário"}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-serif text-2xl text-[color:var(--sun-700)]">
            R$ {selected ? selected.price.toFixed(2).replace('.',',') : '0,00'}
          </span>
          <button disabled={busy || !slot || !serviceId} onClick={confirm} className="btn-gold" data-testid="btn-confirm-booking">
            {busy ? "Confirmando..." : "Confirmar agendamento"}
          </button>
        </div>
      </div>
    </div>
  );
}
